import React, { useState } from 'react';
import { Form, Input, Select, Button, Space, Row, Col, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';

const { TextArea } = Input;
const { Text } = Typography;

const CATEGORIES = ['escalation', 'reclamation', 'suggestion', 'order', 'other'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const CreateTicketModal = ({ onClose, onSubmit, mode = 'create', initialData = null }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    const handleFinish = async (values) => {
        setSubmitting(true);
        try {
            const payload = {
                ...(mode === 'edit' && initialData?.id ? {} : {}),
                subject: values.subject,
                category: values.category,
                priority: values.priority,
                description: values.description,
                ...(mode === 'create' ? {
                    contact_id: values.contact_id || undefined,
                    opened_by_type: values.opened_by_type || 'user',
                } : {
                    status: values.status,
                }),
            };

            await onSubmit(
                mode === 'edit' ? { ...payload, id: initialData?.id } : payload,
                () => setSubmitting(false)
            );
        } catch {
            setSubmitting(false);
        }
    };

    return (
        <ResponsiveModal
            open
            title={mode === 'edit' ? t('edit_ticket') : t('create_ticket')}
            onCancel={onClose}
            desktopWidth={600}
            footer={
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={onClose}>
                        {t('cancel')}
                    </Button>
                    <Button
                        type="primary"
                        loading={submitting}
                        onClick={() => form.submit()}
                    >
                        {mode === 'edit' ? t('update') : t('create')}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={mode === 'edit' && initialData ? {
                    subject: initialData.subject,
                    category: initialData.category,
                    priority: initialData.priority,
                    description: initialData.description,
                    status: initialData.status,
                } : {
                    category: 'other',
                    priority: 'medium',
                    opened_by_type: 'user',
                }}
            >
                <Form.Item
                    name="subject"
                    label={<span>{t('ticket_subject')} <Text type="danger">*</Text></span>}
                    rules={[{ required: true, message: t('subject_required') }]}
                >
                    <Input placeholder={t('enter_ticket_subject')} maxLength={255} />
                </Form.Item>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="category"
                            label={t('ticket_category')}
                        >
                            <Select>
                                {CATEGORIES.map(cat => (
                                    <Select.Option key={cat} value={cat}>
                                        {t(`ticket_category_${cat}`) || cat}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="priority"
                            label={t('ticket_priority')}
                        >
                            <Select>
                                {PRIORITIES.map(pri => (
                                    <Select.Option key={pri} value={pri}>
                                        {t(`ticket_priority_${pri}`) || pri}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                {mode === 'edit' && (
                    <Form.Item
                        name="status"
                        label={t('ticket_status')}
                    >
                        <Select>
                            {['open', 'in_progress', 'resolved', 'closed'].map(s => (
                                <Select.Option key={s} value={s}>
                                    {t(`ticket_status_${s}`) || s}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item
                    name="description"
                    label={t('ticket_description')}
                >
                    <TextArea
                        rows={4}
                        placeholder={t('enter_ticket_description')}
                        maxLength={5000}
                        showCount
                    />
                </Form.Item>

                {mode === 'create' && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                        <Text strong style={{ display: 'block', marginBottom: '16px' }}>
                            {t('advanced', 'Advanced Options')}
                        </Text>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="contact_id"
                                    label={t('contact_id')}
                                >
                                    <Input placeholder={t('enter_contact_id')} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="opened_by_type"
                                    label={t('opened_by_type')}
                                >
                                    <Select>
                                        <Select.Option value="user">{t('user')}</Select.Option>
                                        <Select.Option value="agent">{t('agent')}</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                )}
            </Form>
        </ResponsiveModal>
    );
};

export default CreateTicketModal;
